import { createSlice } from '@reduxjs/toolkit';
import { Reducers } from '@sda/enums/store';
import { createEntityAdapter } from '@client/store/entity.adapter';
import { extractSharedActions } from '@sda/utils/store';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AlgoliaBeacon, GeolocHit } from '@sda/helpers/algolia.helpers';
import type { Bounds } from '@sda/utils/location/interfaces';

export interface HitsState {
  readonly extraHits: GeolocHit<AlgoliaBeacon>[];
  readonly hoverId: string;
  readonly map: InstanceType<typeof google.maps.Map>;
  readonly bounds: Bounds;
  readonly zoom: number;
}

const initialState: HitsState = {
  extraHits: [],
  map: null,
  bounds: null,
  hoverId: null,
  zoom: 7,
};

export const mapAdapter = createEntityAdapter<GeolocHit<AlgoliaBeacon>>({
  selectId: (item) => item.objectID,
});

const mapSlice = createSlice({
  initialState: mapAdapter.getInitialState<HitsState>(initialState),
  name: Reducers.MAP,
  reducers: {
    ...extractSharedActions(mapAdapter),
    resetMapState: (state) => {
      mapAdapter.removeAll(state);

      state.map = null;
      state.extraHits = [];
      state.bounds = null;
      state.hoverId = null;
    },
    setAll: mapAdapter.setAll,
    setBounds: (state, action: PayloadAction<Bounds>) => ({
      ...state,
      bounds: action.payload,
    }),
    setHoverId: (state, action: PayloadAction<string>) => ({
      ...state,
      hoverId: action.payload,
    }),
    setMap: (state, action: PayloadAction<HitsState['map']>) => ({
      ...state,
      map: action.payload,
    }),
    setZoom: (state, action: PayloadAction<number>) => ({
      ...state,
      zoom: action.payload,
    }),
    setExtraHits: (state, action: PayloadAction<GeolocHit<AlgoliaBeacon>[]>) => {
      const extraHits = action.payload;

      return {
        ...state,
        extraHits,
      };
    },
  },
});

export const {
  setAll: setHits, setExtraHits, setStatus: setMapStatus, setBounds,
  setMap, setHoverId, setZoom, resetMapState,
} = mapSlice.actions;

export default mapSlice.reducer;
